import { useRef, useLayoutEffect } from "react";
import "./slider3.css";

interface Slider3Props {
  title: string;
  options: string[];
  selectedIndex: number;
  onChange: (index: number) => void;
  onPositionChange?: (centerY: number) => void;
  disabled?: boolean;
  tooltip?: React.ReactNode;
}

const Slider3: React.FC<Slider3Props> = ({
  title,
  options,
  selectedIndex,
  onChange,
  onPositionChange,
  disabled = false,
  tooltip,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (trackRef.current && onPositionChange) {
      const rect = trackRef.current.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      onPositionChange(centerY);
    }
  }, [onPositionChange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange(Number(event.target.value));
    }
  };

  const percentage =
    options.length > 0 ? ((selectedIndex + 1) / (options.length + 1)) * 100 : 0;

  return (
    <div className={`slider3-container ${disabled ? "slider3-disabled" : ""}`}>
      <div className="slider3-header">
        <div className="slider3-title-with-tooltip">
          <h4 className="slider3-title">{title}</h4>
          {tooltip}
        </div>
      </div>
      <div className="slider3-track-container">
        <input
          type="range"
          min={0}
          max={options.length - 1}
          step={1}
          value={selectedIndex}
          onChange={handleChange}
          className="slider3-input"
          disabled={disabled}
        />
        <div
          ref={trackRef}
          className="slider3-track"
          style={
            { "--slider3-progress": `${percentage}%` } as React.CSSProperties
          }
        >
          <div className="slider3-progress"></div>
          {options.map((option, index) => {
            const tickPosition = ((index + 1) / (options.length + 1)) * 100;
            const isReached = index <= selectedIndex;
            const isSelected = index === selectedIndex;
            return (
              <div key={index}>
                <div
                  className={`slider3-tick ${
                    isReached
                      ? "slider3-tick-reached"
                      : "slider3-tick-unreached"
                  }`}
                  style={{ left: `${tickPosition}%` }}
                />
                <div
                  className={`slider3-tick-label ${
                    isSelected ? "slider3-tick-label-selected" : ""
                  }`}
                  style={{ left: `${tickPosition}%` }}
                >
                  {option}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Slider3;
